import { DEFAULT_ZOOM } from '../../constants';
import type {
  Location,
  PinIconInterface,
  PinSymbolInterface,
} from '../../GoogleMap.types';

export const createSdkMethods = (
  mapRef: React.RefObject<google.maps.Map>,
  updateMapState?: (center: google.maps.LatLngLiteral, zoom: number) => void,
) => {
  return {
    setMapCenter: (longitude: number, latitude: number) => {
      const center = { lat: latitude, lng: longitude };

      if (mapRef.current) {
        // Use direct map methods without triggering React state updates
        // This prevents re-renders during programmatic changes
        mapRef.current.setCenter(center);
        // Update map state if provided
        if (updateMapState) {
          updateMapState(center, mapRef.current.getZoom() || DEFAULT_ZOOM);
        }
      }

      return Promise.resolve();
    },

    setMapZoom: (zoom: number) => {
      if (mapRef.current) {
        // Use direct map methods without triggering React state updates
        mapRef.current.setZoom(zoom);
        // Update map state if provided
        if (updateMapState) {
          const center = mapRef.current.getCenter();
          if (center) {
            updateMapState({ lat: center.lat(), lng: center.lng() }, zoom);
          }
        }
      }

      return Promise.resolve();
    },

    fitBounds: (bounds: {
      north: number;
      south: number;
      east: number;
      west: number;
    }) => {
      if (mapRef.current) {
        const googleBounds = new google.maps.LatLngBounds(
          { lat: bounds.south, lng: bounds.west },
          { lat: bounds.north, lng: bounds.east },
        );

        // Use direct map methods without triggering React state updates
        mapRef.current.fitBounds(googleBounds);
        // Update map state if provided
        if (updateMapState) {
          const center = mapRef.current.getCenter();
          const zoom = mapRef.current.getZoom();
          if (center && zoom !== undefined) {
            updateMapState({ lat: center.lat(), lng: center.lng() }, zoom);
          }
        }
      }
      return Promise.resolve();
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setMarkers: (locations: Array<Location>) => {
      // TODO: Implement marker setting logic
      return Promise.resolve();
    },

    getVisibleMarkers: (locations: Array<Location>) => {
      if (mapRef.current) {
        const bounds = mapRef.current.getBounds();
        if (bounds) {
          const visibleMarkers = locations
            .filter(marker => {
              const position = new google.maps.LatLng(
                marker.latitude,
                marker.longitude,
              );
              return bounds.contains(position);
            })
            .map(marker => ({
              title: marker.title,
              location: {
                latitude: marker.latitude,
                longitude: marker.longitude,
              },
            }));
          return Promise.resolve(visibleMarkers);
        }
      }

      return Promise.resolve(
        locations.map(loc => ({
          title: loc.title,
          location: {
            latitude: loc.latitude,
            longitude: loc.longitude,
          },
        })),
      );
    },

    setMarkerIcon: (_updateData: {
      coordinates: {
        latitude: Location['latitude'];
        longitude: Location['longitude'];
      };
      iconOptions: PinIconInterface | PinSymbolInterface;
      locations: Array<Location>;
    }) => {
      // TODO implement setMarkerIcon
      return Promise.resolve();
    },
  };
};
